import { useState, useEffect } from 'react';
import { FaBook, FaCalendarAlt, FaClock, FaChevronDown, FaChevronUp, FaCog } from 'react-icons/fa';
import logo from './assets/logo.png'
import { useNavigate } from 'react-router-dom';
import SettingsModal from '../components/SettingsModal';

// Import modular components
import OrientationModule from '../components/OrientationModule';
import SpeakingModule from '../components/SpeakingModule';
import ListeningModule from '../components/ListeningModule';
import ReadingModule from '../components/ReadingModule';
import WritingModule from '../components/WritingModule';

import '../App.css';

function Home() {
    // Restore state from sessionStorage if available
    const getInitialState = (key, defaultValue) => {
        const stored = sessionStorage.getItem(key);
        return stored ? JSON.parse(stored) : defaultValue;
    };

    const [expandedModules, setExpandedModules] = useState(() => getInitialState('expandedModules', {}));
    const [expandedClasses, setExpandedClasses] = useState(() => getInitialState('expandedClasses', {}));
    const [expandedSubClasses, setExpandedSubClasses] = useState(() => getInitialState('expandedSubClasses', {}));
    const [expandedSubSubClasses, setExpandedSubSubClasses] = useState(() => getInitialState('expandedSubSubClasses', {}));
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [courseSettings, setCourseSettings] = useState(null);
    const navigate = useNavigate();

    // Load course settings from localStorage on component mount
    useEffect(() => {
        const savedSettings = localStorage.getItem('course_settings');
        if (savedSettings) {
            setCourseSettings(JSON.parse(savedSettings));
        }
    }, []);

    // Persist state to sessionStorage on change
    useEffect(() => {
        sessionStorage.setItem('expandedModules', JSON.stringify(expandedModules));
    }, [expandedModules]);
    useEffect(() => {
        sessionStorage.setItem('expandedClasses', JSON.stringify(expandedClasses));
    }, [expandedClasses]);
    useEffect(() => {
        sessionStorage.setItem('expandedSubClasses', JSON.stringify(expandedSubClasses));
    }, [expandedSubClasses]);
    useEffect(() => {
        sessionStorage.setItem('expandedSubSubClasses', JSON.stringify(expandedSubSubClasses));
    }, [expandedSubSubClasses]);

    const handleSettingsSave = (settings) => {
        setCourseSettings(settings);
    };

    const courseData = {
        name: "HEXA'S Education CD IELTS (Academic)",
        startDate: courseSettings?.startDate || "",
        duration: "30 Days",
        batchTime: courseSettings ? `${courseSettings.batchStartTime} - ${courseSettings.batchEndTime}` : "",
        modules: [
            {
                name: "Orientation Class",
                classes: [
                    { name: "Introduction to IELTS Academic Course" },
                    { name: "CD IELTS : Challenges & Opportunities" },
                    { name: "CD IELTS : Strategies to overcome the barriers" }
                ]
            },
            {
                name: "Speaking Module",
                classes: [
                    { 
                        name: "Class 01: Part 01",
                        subClasses: ["Class Lecture: HEXA'S IELTS Preparation Coursebook (Speaking)", "Guided Practice 01", "Guided Practice 02"]
                    },
                    { 
                        name: "Class 02: Part 02 (Cue Card)",
                        subClasses: ["Class Lecture: HEXA'S IELTS Preparation Coursebook (Speaking)", "Guided Practice 01", "Guided Practice 02"]
                    },
                    { 
                        name: "Class 03: Part 03",
                        subClasses: ["Class Lecture: HEXA'S IELTS Preparation Coursebook (Speaking)", "Guided Practice 01", "Guided Practice 02"]
                    }
                ]
            },
            {
                name: "Listening Module",
                classes: [
                    {
                        name: "Class 04: Basics + Listening Part 01",
                        subClasses: ["Form Completion", "Note Completion", "Table Completion"]
                    },
                    {
                        name: "Class 05: Part 04",
                        subClasses: ["Note Completion"]
                    },
                    {
                        name: "Class 06: Single M.C.Qs",
                        subClasses: ["Part 2", "Part 3"]
                    },
                    {
                        name: "Class 07: Double M.C.Qs & Flowchart",
                        subClasses: ["Part 2", "Part 3", "Flowchart"]
                    },
                    {
                        name: "Class 08: Matching Information",
                        subClasses: ["Part 2", "Part 3"]
                    },
                    {
                        name: "Class 09: Maps & Diagrams",
                        subClasses: ["Part 2"]
                    }
                ]
            },
            {
                name: "Reading Module",
                classes: [
                    {
                        name: "Class 10: Reading Basics & Completions",
                        subClasses: [
                            {
                                name: "Flowchart Completion",
                                subSubClasses: [
                                    { name: "Practice 01: Space: The Final Archaeological Frontier", route: "class-10-flowchart-practice-01" },
                                    { name: "Practice 02: The Forgotten Forest", route: "class-10-flowchart-practice-02" }
                                ],
                                route: "class-10-flowchart"
                            },
                            {
                                name: "Short Question Answer",
                                subSubClasses: [
                                    { name: "Practice 01: Early Telecommunication Devices", route: "class-10-shortq-practice-01" }
                                ],
                                route: "class-10-shortq"
                            }
                        ],
                        route: "class-10"
                    },
                    {
                        name: "Class 11: Completion Questions",
                        subClasses: [
                            { name: "Diagram Completion", route: "class-11-diagram" },
                            { name: "Note Completion", route: "class-11-note" },
                            { name: "Sentence Completion", route: "class-11-sentence" },
                            { name: "Table Completion", route: "class-11-table" }
                        ],
                        route: "class-11"
                    },
                    {
                        name: "Class 12: Summary Completion",
                        subClasses: [
                            { name: "with Clues", route: "class-12-with-clues" },
                            { name: "without Clues", route: "class-12-without-clues" }
                        ],
                        route: "class-12"
                    },
                    {
                        name: "Class 13: True / False / Not Given",
                        subClasses: [
                            {
                                name: "True / False / Not Given",
                                subSubClasses: [
                                    { name: "Practice 01: The Accidental Rainforest", route: "class-13-practice-01" },
                                    { name: "Practice 02: Timur Gareyev – blindfold chess champion", route: "class-13-practice-02" },
                                    { name: "Practice 03: Why we need to protect polar bears", route: "class-13-practice-03" }
                                ],
                                route: "class-13-tfng"
                            }
                        ],
                        route: "class-13"
                    },
                    { name: "Class 14: Yes / No / Not Given" },
                    { name: "Class 15: M.C.Qs" },
                    {
                        name: "Class 16: Matching",
                        subClasses: ["Information", "Sentence Endings"]
                    },
                    {
                        name: "Class 17: Matching - People",
                        subClasses: ["Features", "People"]
                    },
                    {
                        name: "Class 18: Matching",
                        subClasses: ["Headings"]
                    }
                ]
            },
            {
                name: "Writing Module",
                classes: [
                    {
                        name: "Class 19: Introduction to Writing",
                        subClasses: ["Task 1 & Task 2 Overview", "Challenges and Strategies"]
                    },
                    {
                        name: "Class 20: Task 01 - Line Charts",
                        subClasses: ["Dynamic"]
                    },
                    {
                        name: "Class 21: Task 01 - Bar Charts",
                        subClasses: ["Static", "Dynamic"]
                    },
                    {
                        name: "Class 22: Task 01 - Pie Charts",
                        subClasses: ["Static", "Dynamic"]
                    },
                    {
                        name: "Class 23: Task 01 - Table Charts",
                        subClasses: ["Static", "Dynamic"]
                    },
                    {
                        name: "Class 24: Task 01 - Combined Charts",
                        subClasses: ["Line & Bar", "Pie & Table"]
                    },
                    {
                        name: "Class 25: Task 01 - Diagrams",
                        subClasses: ["Man-made Process", "Natural Process"]
                    },
                    {
                        name: "Class 26: Task 01 - Maps",
                        subClasses: ["Past & Present", "Present & Future"]
                    },
                    {
                        name: "Class 27: Task 02 - Essay Types",
                        subClasses: ["Problem–Solution", "Cause–Solution", "Cause–Problem"]
                    },
                    { name: "Class 28: Task 02 - Advantages–Disadvantages" },
                    {
                        name: "Class 29: Task 02 - Discussion Essays",
                        subClasses: ["Discuss Both Views", "Agree–Disagree"]
                    },
                    {
                        name: "Class 30: Task 02 - Two-Part Questions",
                        subClasses: ["Positive–Negative Development"]
                    }
                ]
            }
        ]
    };

    const toggleModule = (moduleIndex) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleIndex]: !prev[moduleIndex]
        }));
    };

    const toggleClass = (moduleIndex, classIndex) => {
        setExpandedClasses(prev => ({
            ...prev,
            [`${moduleIndex}-${classIndex}`]: !prev[`${moduleIndex}-${classIndex}`]
        }));
    };

    const toggleSubClass = (moduleIndex, classIndex) => {
        const key = `${moduleIndex}-${classIndex}`;
        setExpandedSubClasses(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleSubSubClass = (parentKey, subIndex) => {
        const key = `${parentKey}-${subIndex}`;
        setExpandedSubSubClasses(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Function to render the appropriate module component
    const renderModule = (module, moduleIndex) => {
        const commonProps = {
            module,
            moduleIndex,
            expandedModules,
            toggleModule,
            expandedClasses,
            toggleClass,
            expandedSubClasses,
            toggleSubClass,
            expandedSubSubClasses,
            toggleSubSubClass
        };

        switch (module.name) {
            case 'Orientation Class':
                return <OrientationModule {...commonProps} />;
            case 'Speaking Module':
                return <SpeakingModule {...commonProps} />;
            case 'Listening Module':
                return <ListeningModule {...commonProps} />;
            case 'Reading Module':
                return <ReadingModule {...commonProps} />;
            case 'Writing Module':
                return <WritingModule {...commonProps} />;
            default:
                return <OrientationModule {...commonProps} />;
        }
    };

    return (
        <div className="relative min-h-screen p-8 bg-gradient-to-br from-[#f5f7fa] to-[#e4e8f0] overflow-hidden">
            {/* Background spinning logo */}
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vh] opacity-[0.03] z-0 pointer-events-none">
                <img
                    src={logo}
                    alt="Hexas Logo"
                    className="w-full h-full object-contain animate-spin-slow"
                />
            </div>

            {/* Header Section */}
            <header className="relative z-10 bg-white/95 p-8 rounded-xl shadow-lg mb-8 backdrop-blur-md">
                <div className="flex flex-col items-center">
                    {/* Logo centered */}
                    <img
                        src={logo}
                        alt="Hexas Logo"
                        className="h-16 w-auto object-contain rounded-lg shadow-md bg-white px-2 py-1 mb-4"
                    />
                    <h1 className="text-2xl font-bold flex items-center justify-center gap-4 w-full max-w-4xl mx-auto">
                        <FaBook /> {courseData.name}
                    </h1>
                </div>

                <div className="flex gap-8 mt-4 text-lg justify-center">
                    <div className="flex items-center gap-2">
                        <FaCalendarAlt /> Start Date: {courseData.startDate || "Not set"}
                    </div>
                    <div className="flex items-center gap-2">
                        <FaClock /> Duration: {courseData.duration}
                    </div>
                    {courseData.batchTime && (
                        <div className="flex items-center gap-2">
                            <FaClock /> Batch Time: {courseData.batchTime}
                        </div>
                    )}
                </div>

                {/* Settings Icon */}
                <button
                    onClick={() => setShowSettingsModal(true)}
                    className="absolute top-4 right-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                    title="Course Settings"
                >
                    <FaCog size={20} />
                </button>
            </header>

            {/* Modules */}
            <div className="space-y-4 relative z-10">
                {courseData.modules.map((module, moduleIndex) => (
                    <div key={moduleIndex}>
                        {renderModule(module, moduleIndex)}
                    </div>
                ))}
            </div>

            {/* Settings Modal */}
            <SettingsModal
                isOpen={showSettingsModal}
                onClose={() => setShowSettingsModal(false)}
                onSave={handleSettingsSave}
            />
        </div>
    );
}

export default Home;
